import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import Button from '@/components/Button';
import ChannelsFilter from '@/components/ChannelsFilter';
import { Controls, ControlsButton } from '@/components/Controls';
import InfiniteLoader from '@/components/InfiniteLoader';
import InfoTooltip from '@/components/InfoTooltip';
import { SectionHeading, SrOnly } from '@/components/Layout/Parts';
import {
  ResponsiveTable,
  Table,
  TableContent,
  TableHeader,
  Th,
} from '@/components/Table';
import Text from '@/components/Text';
import TopicsCreationDropdown from '@/components/TopicsCreationDropdown';
import useChannelsFilter from '@/hooks/useChannelsFilter';
import useDidUpdate from '@/hooks/useDidUpdate';
import { selectTeamSettings } from '@/selectors';
import { PaginatedResponse, UnassignedVideoResource } from '@/types/resources';
import { api } from '@/utils/api';
import { AssignTopicsStatus } from '@/utils/enums';
import { numFormatter } from '@/utils/helpers';
import { Dispatch, RootState } from '@/utils/store';

import AssignTopicsFilter from './AssignTopicsFilter';
import AssignTopicsTableRow from './AssignTopicsTableRow';
import Filter from './Filter';

const StyledSectionHeading = styled(SectionHeading)`
  margin: 0;
  flex: 1;
`;

export const AddButtonTopics = styled(Button)`
  margin-left: 12px;
  &:first-child {
    margin-left: 0;
  }
`;

const LiveVideosControls = styled(Controls)`
  flex-shrink: 0;
  margin-top: 8px;
  border: 1px solid rgba(196, 196, 196, 0.2);
  border-radius: 4px;
  padding: 4px;
`;

const FilterHolder = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
  gap: 16px;
`;

const FiltersHolder = styled.div`
  display: flex;
  gap: 8px;
`;

const StyledTable = styled(Table)`
  th,
  td {
    padding: 16px 12px;
    font-size: 11px;
    vertical-align: top;
    &:nth-child(1) {
      width: 11.5%;
      padding-left: 24px;
    }
    &:nth-child(2) {
      width: calc(27.5% - 56px);
    }
    &:nth-child(3) {
      width: 15.5%;
    }
    &:nth-child(4) {
      width: 15.5%;
    }
    &:nth-child(5) {
      width: 15.5%;
    }
    &:nth-child(6) {
      width: 15.5%;
    }
    &:nth-child(7) {
      width: 56px;
      position: relative;
    }
  }
`;

const periods = ['1 day', '7 days', '1 month', '3 months', '1 year', 'All'];

const AssignTopicsTable = () => {
  const dispatch = useDispatch<Dispatch>();

  const organisationName = useSelector(
    (rootState: RootState) => rootState.auth.user!.organisation!.name,
  );

  const userRole = useSelector(
    (rootState: RootState) => rootState.auth.user!.role.name,
  );

  const tabs = [
    { value: 1, title: 'Competitors' },
    { value: 0, title: `${organisationName}` },
    { value: null, title: 'All' },
  ];

  const [activeTab, setActiveTab] = useState(tabs[0]);

  const [showLiveVideos, setShowLiveVideos] = useState(false);

  const [activeFilter, setActiveFilter] = useState(AssignTopicsStatus.DEFAULT);

  const { teamId } = useParams();

  const teamSettings = useSelector((rootState: RootState) =>
    selectTeamSettings(rootState, teamId!),
  );

  const [activePeriod, setActivePeriod] = useState(
    teamSettings?.assign_topics_period ?? periods[1],
  );

  const onPeriodChange = (period: string) => {
    setActivePeriod(period);
  };

  const [filteredChannelsIds, filteredChannelsKey] = useChannelsFilter();

  const fetchVideos = ({ pageParam }: { pageParam?: string }) => {
    return api.get<never, PaginatedResponse<UnassignedVideoResource>>(
      pageParam ?? `/v1/teams/${teamId}/videos/unassigned`,
      {
        params: !pageParam
          ? {
              ...(!!teamSettings?.assign_topics_min_views && {
                'filter[minimum_views]': teamSettings.assign_topics_min_views,
              }),
              ...(!!activeFilter &&
                activeFilter !== AssignTopicsStatus.DEFAULT && {
                  'filter[topics_status]': activeFilter,
                }),
              ...(!!teamSettings?.assign_topics_min_v7_index && {
                'filter[v7_index_percentage]':
                  teamSettings.assign_topics_min_v7_index,
              }),
              ...(!!teamSettings?.assign_topics_min_duration && {
                'filter[min_duration]': teamSettings.assign_topics_min_duration,
              }),
              ...(!!teamSettings?.assign_topics_max_duration && {
                'filter[max_duration]': teamSettings.assign_topics_max_duration,
              }),
              ...(activeTab.value !== null && { competitor: activeTab.value }),
              ...(filteredChannelsIds?.length && {
                'filter[channel_id]': filteredChannelsIds.join(','),
              }),
              ...(activePeriod !== 'All' && { 'filter[period]': activePeriod }),
              ...(!showLiveVideos && {
                'filter[was_live]': 0,
              }),
            }
          : {},
      },
    );
  };

  const videosQueryKey = [
    'unassigned_videos',
    { tab: activeTab.value, teamId, filteredChannelsKey, period: activePeriod },
  ];

  const {
    data,
    isError,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    remove,
    refetch,
    fetchNextPage,
  } = useInfiniteQuery<PaginatedResponse<UnassignedVideoResource>>(
    videosQueryKey,
    fetchVideos,
    {
      staleTime: 120 * 1000,
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
    },
  );

  useDidUpdate(() => {
    remove();
    refetch();
    dispatch.navigationCounter.getAssignTopicsCounter(teamId!);
  }, [teamSettings, activePeriod, activeFilter, showLiveVideos]);

  useDidUpdate(() => {
    if (activeTab.value === 0) {
      remove();
      refetch();
    }
  }, [filteredChannelsKey]);

  const videos = useMemo(
    () =>
      Object.keys(data?.pages || {}).reduce<UnassignedVideoResource[]>(
        (acc, currKey: any) => {
          return [...acc, ...data!.pages[currKey].data];
        },
        [],
      ),
    [data],
  );

  return (
    <>
      <TableHeader>
        <Controls>
          {tabs.map((tab, index) => (
            <ControlsButton
              key={index}
              active={tab.value === activeTab.value}
              onClick={() => setActiveTab(tab)}
            >
              {tab.title}
            </ControlsButton>
          ))}
        </Controls>
        <TopicsCreationDropdown />
      </TableHeader>
      <ChannelsFilter />
      {teamId !== 'all' && (
        <FilterHolder>
          <Filter assignTopicsPeriod={activePeriod} />
          <div>
            <Text $type="smallHeadline">Live Videos</Text>
            <LiveVideosControls>
              <ControlsButton
                active={!showLiveVideos}
                onClick={() => setShowLiveVideos(false)}
                gray
              >
                Hide
              </ControlsButton>
              <ControlsButton
                active={showLiveVideos}
                onClick={() => setShowLiveVideos(true)}
              >
                Show
              </ControlsButton>
            </LiveVideosControls>
          </div>
        </FilterHolder>
      )}
      <TableHeader>
        <StyledSectionHeading>
          {activeTab.title} videos: without topics
          {!!teamSettings?.assign_topics_min_views && (
            <>
              {' '}
              & at least{' '}
              {numFormatter(
                parseInt(teamSettings.assign_topics_min_views),
                0,
              )}{' '}
              views
            </>
          )}
        </StyledSectionHeading>
        <FiltersHolder>
          <Controls>
            {periods.map((period, index) => (
              <ControlsButton
                key={index}
                active={period === activePeriod}
                onClick={() => onPeriodChange(period)}
              >
                {period}
              </ControlsButton>
            ))}
          </Controls>
          <AssignTopicsFilter
            activeFilter={activeFilter}
            setActiveFilter={setActiveFilter}
          />
        </FiltersHolder>
      </TableHeader>
      <ResponsiveTable>
        <StyledTable $fixed>
          <thead>
            <tr>
              <Th>Thumbnail</Th>
              <Th>
                Video title
                <br />
                <Text as="span" $color="gray" $type="extraSmall">
                  Channel name
                </Text>
              </Th>
              <Th>
                Script Topics
                <InfoTooltip
                  title="Script Topics"
                  content="The main topic of the video, should be added after watching the video"
                />
              </Th>
              <Th>
                Thumb Topics
                <InfoTooltip
                  title="Thumb Topics"
                  content="Prominent elements in the thumbnail, that create the first impression about the video"
                />
              </Th>
              <Th>
                Title Topics
                <InfoTooltip
                  title="Title Topics"
                  content="Automatically assigned tags, added periodically by analyzing title"
                />
              </Th>
              <Th>
                Video Type
                <InfoTooltip
                  title="Video Type"
                  content='Flexible field to group videos into categories like "animation", "reuse", etc'
                />
              </Th>
              <Th>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              colSpan={6}
              isError={isError}
              hasData={!!videos.length}
            >
              {videos.map((video) => (
                <AssignTopicsTableRow
                  key={video.id}
                  video={video}
                  videosQueryKey={videosQueryKey}
                  userRole={userRole}
                />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ResponsiveTable>
      {!isLoading && (
        <InfiniteLoader
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          isFetchingNextPage={isFetchingNextPage}
        />
      )}
    </>
  );
};

export default AssignTopicsTable;
